import { Component, computed, effect, inject, signal, ViewChild } from '@angular/core';
import { NavigationService } from 'src/app/services/navigation.service';
import { DataService } from 'src/app/services/data.service';
import { IProjekt } from 'src/app/types/IProjekt';
import { MatDialog } from '@angular/material/dialog';
import { ProjektAntragComponent } from '../projekt-antrag/projekt-antrag.component';
import { DialogService } from 'src/app/services/dialog.service';
import { HttpErrorResponse } from '@angular/common/http';
import { DxDataGridComponent } from 'devextreme-angular';
import { GridService } from 'src/app/services/controls/grid.service';
import { AppstateService } from 'src/app/services/appstate.service';
import { Pages } from 'src/app/types/infrastructure/enums/Pages';
import { WirtschaftsplanErstellenComponent } from '../wirtschaftsplan-erstellen/wirtschaftsplan-erstellen.component';
import { UserService } from 'src/app/services/user.service';
import type { Column } from "devextreme/ui/data_grid";
import { DestroyRef } from '@angular/core';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';

@Component({
	selector: 'app-projekt-liste',
	templateUrl: './projekt-liste.component.html',
	styleUrl: './projekt-liste.component.css'
})
export class ProjektListeComponent {

	@ViewChild(DxDataGridComponent, { static: false })
	public dataGrid!: DxDataGridComponent;
	public projekte = computed<IProjekt[]>(() => this.dataService.projekte());
	public infoText = computed(() => ((this.gridService.FilterTextNavigationProjekt() + this.gridFilterText()) ? 'Filterung : ' + this.gridService.FilterTextNavigationProjekt() + this.gridFilterText() : ''))
	public gridFilterText = signal('');
	public dataCountText = signal('');
	public showFilterRow = signal(false);
	public showHeaderFilter = signal(true);
	public dialog = inject(MatDialog);

	private readonly gridStateKey = "projektListeGridState";

	constructor(public appStateService: AppstateService,
		private navigationService: NavigationService,
		private dialogService: DialogService,
		public gridService: GridService,
		private dataService: DataService,
		public userService: UserService) {

		effect(() => {
			if (this.projekte() == null) {
				this.dataGrid.instance.beginCustomLoading("Projektliste wird geladen...");
			} else {
				this.dataGrid.instance.endCustomLoading();
			}
		});
	}


	private readonly destroyRef = inject(DestroyRef);

	ngAfterViewInit(): void {
		this.destroyRef.onDestroy(() => {
			// Seite/Component wird verlassen -> Grid-State sichern
			this.gridService.saveGridState(this.dataGrid, this.appStateService.gridStateProjekte);
		});

		
	}


	onGridInitialized(e: any) {
		// Restore saved grid state as early as possible (columns order/width + filter)
		this.gridService.loadGridState(this.dataGrid, this.appStateService.gridStateProjekte);

		// Backward compatibility with old filter-only storage (if present)
		// if (this.appStateService.projektFilter()) {
		//   const savedFilter = JSON.parse(this.appStateService.projektFilter?.());
		//   if (savedFilter) {
		//     this.dataGrid.instance.option("filterValue", savedFilter);
		//   }
		// }
	}

	onProjektAntragButtonClick() {
		// load data for dialog
		this.appStateService.projektantragInit.set(true);
		const dialogRef = this.dialog.open(ProjektAntragComponent, {
			height: '788px',
			width: '1000px',
			disableClose: true,
			data: { name: 'Projektantrag', title: 'Titel' },
		});

		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				// POST
				this.dataService.createProjektantrag(result).subscribe({
					next: (data) => {
						this.dialogService.showMessageSheet('Der Antrag wurde erfolgreich gestellt');
					},
					error: (err: HttpErrorResponse) => {
					}
				});
			}
			// clear cache
			this.appStateService.projektantragInit.set(false);
		});
	}

	onWirtschaftsplanButtonClick() {
		const dialogRef = this.dialog.open(WirtschaftsplanErstellenComponent, {
			height: '612px',
			width: '1100px',
			disableClose: true,
			data: { name: 'Wirtschaftsplan', title: 'Wirtschaftsplan' },
		});

	}

	onHeaderFilterButtonClick() {
		this.dataGrid.instance.clearFilter();
		//this.showHeaderFilter.set(!this.showHeaderFilter());
	}

	onFilterRowButtonClick() {
		this.showFilterRow.set(!this.showFilterRow());
	}

	getReadableFilter(
		filter: any,
		columns: Array<Column | string>
	): string {
		if (!filter) return "";

		if (Array.isArray(filter[0])) {
			return filter
				.map((f: any) => this.getReadableFilter(f, columns))
				.join(` `);
		}
		else if (typeof filter === "string")
			return [["and", "|"], ["or", "oder"]].find(x => x[0] === filter)![1];;

		if (filter[0] === "!") {
			return `NICHT (${this.getReadableFilter(filter[1], columns)})`;
		}

		const [field, operator, value] = filter;
		const column: Column = columns[field.columnIndex] as Column;
		//const column: Column = columns.find((c) => !(c === undefined) && !(typeof c === "string") &&  c.dataField === field) as Column;
		const caption = column?.caption || field;

		return `${caption} ${operator} "${value}"`;
	}



	onContentReady(e: any) {
		// Filter aus dem Grid abfragen und im AppState speichern
		if (e.component.getCombinedFilter() != null) {
			const currentFilter = JSON.stringify(e.component.getCombinedFilter(true));
			this.appStateService.projektFilter.set(currentFilter);

			var filter = e.component.getCombinedFilter();
			const columns: Array<Column | string> = this.dataGrid.columns; // instance.option("columns");

			var filterText = this.getReadableFilter(filter, columns);
			if (this.gridService.FilterTextNavigationProjekt()) {
				this.gridFilterText.set(' | ' + filterText);
			} else {
				this.gridFilterText.set(filterText);
			}
		} else {
			this.gridFilterText.set('');
			this.appStateService.projektFilter.set('');
		}

		this.dataCountText.set(e.component.totalCount() + ' Projekte');
	}

	isBudgetantragAvailable(options: any) {
		if (options.row.rowType == 'data') {
			if (options.row.data.budgetantragGestellt == true) {
				return false;
			}
		}
		return true;
	}

	onProjektButtonClick(e: any) {
		if (e.row.rowType === "data") {
			this.navigationService.navigateToProjektDetails(e.row.data.projektID);
		}
	}

	onBudgetantragButtonClick(e: any) {
		if (e.row.rowType === "data") {
			this.navigationService.navigateToBudgetantragDashboard(e.row.data.projektID, e.row.data.budgetantragID, Pages.PROJEKTLISTE);
		}
	}

	onDataGridRowDblClick(e: any) {
		if (e.rowType === "data") {
			if (e.data.budgetantragGestellt == true)
				this.navigationService.navigateToBudgetantragDashboard(e.data.projektID, e.data.budgetantragID, Pages.PROJEKTLISTE);
			else
				this.navigationService.navigateToProjektDetails(e.data.projektID);
		}
	}

	async onGridExporting(e: DxDataGridTypes.ExportingEvent) {
			var filename = 'Projektliste ' + new Date().toLocaleDateString() + '.xlsx';
			await this.gridService.exportDxGridToExcel(e.component, {
				worksheetName: 'Projektliste ',
				fileName: filename,
				includeHiddenColumns: false,
				includeHiddenBands: false
			});
			e.cancel = true;
		}

}
